import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { PiHandsClappingLight } from "react-icons/pi";
import ChatScreenHeader from "./chatScreenHeader";
import { HiPaperAirplane, HiDevicePhoneMobile } from "react-icons/hi2";
import { Link, useParams, useNavigate } from "react-router-dom";
import "./chatScreen.css";
// Firebase artık kullanılmıyor - Socket.io kullanılıyor
import {
  sendVenueChatMessage,
  updateUserTypingStatus,
  sendEmojiReaction,
  listenToEmojiReactions,
  sendVibrationReaction,
  listenToVibrationReactions,
} from "../../services/socketChatService";
import { getVenueDetails } from "../../services/api";
import { motion, AnimatePresence } from "framer-motion";

// Imports from optimized modules
import { useChatAuth } from "./hooks/useChatAuth";
import { useChatMessages } from "./hooks/useChatMessages";
import { useChatTyping } from "./hooks/useChatTyping";
import { useEmojiReactions } from "./hooks/useEmojiReactions";
import { useVibrationReactions } from "./hooks/useVibrationReactions";
import ChatMessage from "./components/ChatMessage";
import { quickEmojiOptions, animatedUsers } from "./utils/chatConstants";
import chatSocketService from "../../services/websocket";

// Loading Screen Component
const LoadingScreen = ({ title, subtitle }) => (
  <div className="mobile-chat-container w-full flex items-center justify-center bg-[#e8e9ed]">
    <div className="mobile-chat-inner relative flex flex-col items-center w-full max-w-full bg-white shadow-xl overflow-hidden border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl">
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E94057] mb-4"></div>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">{title}</h2>
        <p className="text-gray-600 text-center">{subtitle}</p>
      </div>
    </div>
  </div>
);

// Error Screen Component
const ErrorScreen = ({ error }) => (
  <div className="mobile-chat-container w-full flex items-center justify-center bg-[#e8e9ed]">
    <div className="mobile-chat-inner relative flex flex-col items-center w-full max-w-full bg-white shadow-xl overflow-hidden border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl">
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <div className="text-red-500 text-6xl mb-4">⚠️</div>
        <h2 className="text-lg font-semibold text-red-600 mb-2">
          Giriş Hatası
        </h2>
        <p className="text-gray-600 text-center mb-4">{error}</p>
        <p className="text-sm text-gray-500 text-center">
          Anasayfaya yönlendiriliyorsunuz...
        </p>
      </div>
    </div>
  </div>
);

// Custom Hook: useVenueDetails
const useVenueDetails = (venueUUID, navigate, setShowModal) => {
  const [venueDetails, setVenueDetails] = useState(null);
  const [venueError, setVenueError] = useState(null);

  useEffect(() => {
    if (!venueUUID) {
      setVenueDetails(null);
      setVenueError(null);
      return;
    }

    const fetchVenueDetails = async () => {
      try {
        const details = await getVenueDetails(venueUUID);
        setVenueDetails(details);
        setVenueError(null);
      } catch (error) {
        console.error("Venue details error:", error);
        
        // HTTP status koduna göre farklı davranışlar
        if (error.status === 500) {
          // 500 hatası: Mekan bulunamadı, anasayfaya yönlendir
          setVenueError("Mekan bulunamadı. Anasayfaya yönlendiriliyorsunuz...");
          setTimeout(() => {
            navigate("/");
          }, 2000);
        } else if (error.status === 403) {
          // 403 hatası: Yetkisiz erişim, giriş popup'ını göster
          setVenueError("Bu mekana erişim için giriş yapmanız gerekiyor.");
          setShowModal(true);
        } else {
          // Diğer hatalar: Default davranış
          setVenueDetails({
            name: "Mekan yükleniyor...",
            logo: "/assets/qriendPhoneTanitim.png",
          });
          setVenueError(null);
        }
      }
    };

    fetchVenueDetails();
  }, [venueUUID, navigate, setShowModal]);

  return { venueDetails, venueError };
};

// Main ChatScreen Component
const ChatScreen = () => {
  const { venueUUID } = useParams();
  const navigate = useNavigate();

  // UI States
  const [inputValue, setInputValue] = useState("");
  const [showEmojiOptions, setShowEmojiOptions] = useState(false);
  const [animatedEmojis, setAnimatedEmojis] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [animatedMessages, setAnimatedMessages] = useState([]);
  const [isVibrating, setIsVibrating] = useState(false);
  const [animatedVibrationMessages, setAnimatedVibrationMessages] = useState(
    {}
  );
  const [processedEmojiMessages, setProcessedEmojiMessages] = useState(
    new Set()
  );
  const [processedVibrationMessages, setProcessedVibrationMessages] = useState(
    new Set()
  );
  const [lastProcessedMessageTime, setLastProcessedMessageTime] = useState(
    Date.now()
  );

  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const lastSendTimeRef = useRef(0);
  const lastEmojiTimeRef = useRef(0);
  const lastVibrationTimeRef = useRef(0);

  // Cooldown kontrolü fonksiyonları
  const isEmojiCooldown = () => {
    return Date.now() - lastEmojiTimeRef.current < 3000;
  };

  const isVibrationCooldown = () => {
    return Date.now() - lastVibrationTimeRef.current < 3000;
  };

  // Optimized Custom Hooks
  const {
    isLoggedIn,
    currentUser,
    isCheckingIn,
    checkInError,
    hasCheckedIn,
    setCurrentUser,
  } = useChatAuth(venueUUID, navigate);

  const shouldRun = venueUUID && hasCheckedIn;

  const {
    messages,
    isLoading,
    error,
    isLoadingMore,
    hasMoreMessages,
    loadMoreMessages,
  } = useChatMessages(shouldRun ? venueUUID : null, hasCheckedIn);

  const typingUsers = useChatTyping(
    shouldRun ? venueUUID : null,
    currentUser,
    inputValue
  );

  const { venueDetails, venueError } = useVenueDetails(venueUUID, navigate, setShowModal);
  const { incomingReactions, sendReaction } = useEmojiReactions(
    shouldRun ? venueUUID : null,
    currentUser
  );
  const { incomingVibrations, sendVibration } = useVibrationReactions(
    shouldRun ? venueUUID : null,
    currentUser
  );

  // Handle incoming emoji reactions from other users
  const [processedReactionIds, setProcessedReactionIds] = useState(new Set());

  useEffect(() => {
    console.log("🎭 incomingReactions effect tetiklendi:", incomingReactions);
    if (incomingReactions.length === 0) return;

    incomingReactions.forEach((reaction) => {
      console.log("🎭 İşlenen reaction:", reaction);
      console.log("🎭 Processed IDs:", processedReactionIds);

      // Duplicate kontrolü
      if (processedReactionIds.has(reaction.id)) {
        console.log("🎭 Duplicate reaction, atlanıyor:", reaction.id);
        return;
      }

      console.log("🎭 Yeni reaction animasyonu tetikleniyor:", reaction.emoji);
      setProcessedReactionIds((prev) => new Set([...prev, reaction.id]));
      triggerFullScreenEmojiAnimation(reaction.emoji, reaction.senderName);

      // 10 saniye sonra processed ID'yi temizle
      setTimeout(() => {
        setProcessedReactionIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(reaction.id);
          return newSet;
        });
      }, 10000);
    });
  }, [incomingReactions, processedReactionIds]);

  // Handle incoming vibration reactions from other users
  const [processedVibrationIds, setProcessedVibrationIds] = useState(new Set());

  useEffect(() => {
    console.log("🚨 incomingVibrations effect tetiklendi:", incomingVibrations);
    if (incomingVibrations.length === 0) return;

    incomingVibrations.forEach((vibration) => {
      console.log("🚨 İşlenen vibration:", vibration);
      console.log("🚨 Processed IDs:", processedVibrationIds);

      // Duplicate kontrolü
      if (processedVibrationIds.has(vibration.id)) {
        console.log("🚨 Duplicate vibration, atlanıyor:", vibration.id);
        return;
      }

      console.log(
        "🚨 Yeni vibration efekti tetikleniyor:",
        vibration.senderName
      );
      setProcessedVibrationIds((prev) => new Set([...prev, vibration.id]));
      triggerVibrationEffect(vibration.senderName);

      // 5 saniye sonra processed ID'yi temizle
      setTimeout(() => {
        setProcessedVibrationIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(vibration.id);
          return newSet;
        });
      }, 5000);
    });
  }, [incomingVibrations, processedVibrationIds]);

  // Titreşim mesajlarının animasyonlarını takip et
  useEffect(() => {
    if (!messages || messages.length === 0) return;

    // Sadece en son mesajı kontrol et (yeni gelen mesaj)
    const lastMessage = messages[messages.length - 1];

    // Mesajın timestamp'i kontrolü - sadece son 10 saniye içindeki mesajlar için animasyon
    const messageTime = new Date(lastMessage.timestamp).getTime();
    const now = Date.now();
    const timeDifference = now - messageTime;
    const isRecentMessage = timeDifference < 10000; // 10 saniye

    if (
      lastMessage.type === "vibration" &&
      !animatedVibrationMessages[lastMessage.id] &&
      !processedVibrationMessages.has(lastMessage.id) &&
      isRecentMessage &&
      messageTime > lastProcessedMessageTime
    ) {
      console.log("💥 Yeni titreşim mesajı tespit edildi:", {
        messageTime: new Date(messageTime),
        timeDifference,
        isRecent: isRecentMessage,
      });

      // Yeni titreşim mesajı geldiğinde animasyonu başlat
      setAnimatedVibrationMessages((prev) => ({
        ...prev,
        [lastMessage.id]: true,
      }));

      // Eğer mesaj başka bir kullanıcıdan geliyorsa, fiziksel titreşim efekti de tetikle
      if (
        currentUser &&
        String(lastMessage.senderId) !== String(currentUser.id)
      ) {
        console.log(
          "💥 Başka kullanıcıdan gelen titreşim mesajı, efekt tetikleniyor:",
          lastMessage.senderName
        );

        setProcessedVibrationMessages(
          (prev) => new Set([...prev, lastMessage.id])
        );

        // Fiziksel titreşim efekti
        setIsVibrating(true);
        if (navigator.vibrate) {
          navigator.vibrate([300, 150, 300, 150, 300]);
        }

        // 1 saniye sonra titreşim efektini kaldır
        setTimeout(() => {
          setIsVibrating(false);
        }, 1000);

        // 5 saniye sonra processed ID'yi temizle
        setTimeout(() => {
          setProcessedVibrationMessages((prev) => {
            const newSet = new Set(prev);
            newSet.delete(lastMessage.id);
            return newSet;
          });
        }, 5000);
      }

      // Son işlenen mesaj zamanını güncelle
      setLastProcessedMessageTime(messageTime);

      // 5 saniye sonra animasyonu durdur
      setTimeout(() => {
        setAnimatedVibrationMessages((prev) => ({
          ...prev,
          [lastMessage.id]: false,
        }));
      }, 5000);
    }
  }, [messages]);

  // Emoji mesajları için animasyon tetikleme
  useEffect(() => {
    if (!messages || messages.length === 0) return;

    // Sadece en son mesajı kontrol et (yeni gelen mesaj)
    const lastMessage = messages[messages.length - 1];

    // Mesajın timestamp'i kontrolü - sadece son 10 saniye içindeki mesajlar için animasyon
    const messageTime = new Date(lastMessage.timestamp).getTime();
    const now = Date.now();
    const timeDifference = now - messageTime;
    const isRecentMessage = timeDifference < 10000; // 10 saniye

    if (
      lastMessage.type === "emoji" &&
      currentUser &&
      String(lastMessage.senderId) !== String(currentUser.id) &&
      !processedEmojiMessages.has(lastMessage.id) &&
      isRecentMessage &&
      messageTime > lastProcessedMessageTime
    ) {
      console.log("🎭 Yeni emoji mesajı tespit edildi:", {
        messageTime: new Date(messageTime),
        timeDifference,
        isRecent: isRecentMessage,
      });

      // Başka kullanıcıdan gelen emoji mesajı için animasyon tetikle
      console.log(
        "🎭 Başka kullanıcıdan gelen emoji mesajı, animasyon tetikleniyor:",
        lastMessage.text
      );
      setProcessedEmojiMessages((prev) => new Set([...prev, lastMessage.id]));
      triggerFullScreenEmojiAnimation(lastMessage.text, lastMessage.senderName);

      // Son işlenen mesaj zamanını güncelle
      setLastProcessedMessageTime(messageTime);

      // 10 saniye sonra processed ID'yi temizle
      setTimeout(() => {
        setProcessedEmojiMessages((prev) => {
          const newSet = new Set(prev);
          newSet.delete(lastMessage.id);
          return newSet;
        });
      }, 10000);
    }
  }, [messages]);

  // Vibration effect function
  const triggerVibrationEffect = (senderName) => {
    console.log("💥 triggerVibrationEffect çağrıldı, sender:", senderName);
    console.log("💥 Mevcut mesajlar:", messages);

    setIsVibrating(true);

    // Web Vibration API - mobil cihazlarda daha sert fiziksel titreşim
    if (navigator.vibrate) {
      console.log("💥 Fiziksel titreşim tetikleniyor");
      navigator.vibrate([300, 150, 300, 150, 300, 150, 300]); // Daha uzun ve sert titreşim paterni
    }

    // Son titreşim mesajını bul ve animasyonunu tetikle
    const vibrationMessages = messages.filter(
      (msg) => msg.type === "vibration"
    );
    console.log("💥 Bulunan titreşim mesajları:", vibrationMessages);

    const lastVibrationMessage = vibrationMessages.pop(); // En son titreşim mesajını al
    console.log("💥 Son titreşim mesajı:", lastVibrationMessage);

    if (lastVibrationMessage) {
      console.log(
        "💥 Titreşim mesajı animasyonu başlatılıyor:",
        lastVibrationMessage.id
      );
      setAnimatedVibrationMessages((prev) => ({
        ...prev,
        [lastVibrationMessage.id]: true,
      }));

      // 5 saniye sonra animasyonu durdur
      setTimeout(() => {
        console.log(
          "💥 Titreşim mesajı animasyonu durduruluyor:",
          lastVibrationMessage.id
        );
        setAnimatedVibrationMessages((prev) => ({
          ...prev,
          [lastVibrationMessage.id]: false,
        }));
      }, 5000);
    } else {
      console.log("💥 Titreşim mesajı bulunamadı!");
    }

    // 1 saniye sonra titreşim efektini kaldır
    setTimeout(() => {
      setIsVibrating(false);
    }, 1000);
  };

  // User presence effect - Socket.io ile otomatik olarak yönetiliyor
  useEffect(() => {
    // Socket.io bağlantısı user presence'ı otomatik olarak yönetiyor
    // Ek bir işlem gerekmiyor
  }, [isLoggedIn, currentUser, venueUUID, hasCheckedIn]);

  // Auto scroll to bottom - sadece yeni mesajlar için
  const [lastMessageId, setLastMessageId] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // ===== TEMİZ SCROLL SİSTEMİ - SIFIRDAN =====

  // Güçlü scroll fonksiyonu - multiple fallback ile
  const scrollToBottom = (smooth = false) => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const doScroll = () => {
      const maxScroll = container.scrollHeight - container.clientHeight;
      if (smooth) {
        container.scrollTo({
          top: maxScroll,
          behavior: "smooth",
        });
      } else {
        // Force instant scroll - multiple fallback
        container.scrollTop = maxScroll;

        // Extra fallback - ensure we're at bottom
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight;
          // Final fallback
          setTimeout(() => {
            container.scrollTop = container.scrollHeight;
          }, 10);
        });
      }
    };

    // İlk denemede scroll yap
    doScroll();

    // Ek güvenlik için biraz sonra tekrar yap
    setTimeout(doScroll, 20);
  };

  // 1. İlk yükleme scroll'u - daha agresif
  useEffect(() => {
    if (!isLoading && messages.length > 0 && isInitialLoad) {
      // Birden fazla timing ile deneme
      const timers = [100, 200, 300, 500].map((delay) =>
        setTimeout(() => {
          scrollToBottom(false);
        }, delay)
      );

      // İlk başarılı scroll'da diğerlerini iptal et
      setTimeout(() => {
        setIsInitialLoad(false);
        timers.forEach((timer) => clearTimeout(timer));
      }, 600);

      return () => timers.forEach((timer) => clearTimeout(timer));
    }
  }, [isLoading, messages, isInitialLoad]);

  // 2. Yeni mesaj scroll'u - daha güçlü
  useEffect(() => {
    if (messages.length === 0 || isInitialLoad || isLoadingMore) return;

    const currentLastMessage = messages[messages.length - 1];
    if (currentLastMessage?.id !== lastMessageId) {
      setLastMessageId(currentLastMessage.id);

      // Anında ve birden fazla timing ile scroll
      scrollToBottom(false);

      // Ek güvenlik scroll'ları
      setTimeout(() => scrollToBottom(false), 50);
      setTimeout(() => scrollToBottom(false), 100);
    }
  }, [messages, lastMessageId, isInitialLoad, isLoadingMore]);

  // Basit infinite scroll koruma
  const [previousScrollHeight, setPreviousScrollHeight] = useState(0);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !isLoadingMore) return;

    if (previousScrollHeight === 0) {
      setPreviousScrollHeight(container.scrollHeight);
    } else {
      // Yeni mesajlar yüklendikten sonra scroll pozisyonunu koru
      const heightDifference = container.scrollHeight - previousScrollHeight;
      container.scrollTop = container.scrollTop + heightDifference;
      setPreviousScrollHeight(0);
    }
  }, [isLoadingMore, messages.length]);

  // Demo messages for non-logged users
  useEffect(() => {
    if (!isLoggedIn) {
      const timer = setTimeout(() => setShowModal(true), 3000);
      return () => clearTimeout(timer);
    }
    if (isLoggedIn) setShowModal(false);
  }, [isLoggedIn]);

  useEffect(() => {
    if (showModal && !isLoggedIn) {
      let userIndex = 0;
      let messageIndex = 0;
      setAnimatedMessages([]);

      const interval = setInterval(() => {
        setAnimatedMessages((prev) => {
          const currentAnimatedUser = animatedUsers[userIndex];
          const now = new Date();
          const time = now.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          });

          const newMsg = {
            id: Date.now() + Math.random(),
            userId: currentAnimatedUser.id,
            name: currentAnimatedUser.name,
            avatar: currentAnimatedUser.avatar,
            text: currentAnimatedUser.messages[messageIndex],
            time,
          };

          messageIndex++;
          if (messageIndex >= currentAnimatedUser.messages.length) {
            messageIndex = 0;
            userIndex = (userIndex + 1) % animatedUsers.length;
          }

          const next = [...prev, newMsg];
          return next.length > 8 ? next.slice(next.length - 8) : next;
        });
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [showModal, isLoggedIn]);

  // Event Handlers
  const handleSendMessage = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || !venueUUID || !isLoggedIn || !currentUser) return;

    // Basit debounce - 500ms içinde tekrar göndermeyi engelle
    const now = Date.now();
    if (now - lastSendTimeRef.current < 500) {
      console.log("⏳ Çok hızlı mesaj gönderimi engellendi");
      return;
    }
    lastSendTimeRef.current = now;

    // Input'u hemen temizle (UX için)
    setInputValue("");

    try {
      // Mesaj gönderilmeden önce typing'i temizle
      await updateUserTypingStatus(
        venueUUID,
        currentUser.id,
        currentUser.name,
        currentUser.avatar,
        false
      );

      const sender = {
        id: currentUser.id,
        name: currentUser.name,
        avatar: currentUser.avatar,
      };

      // DÜZELTME: Güncel socket ID'yi kullan
      const currentSocketUser = chatSocketService.user;
      if (currentSocketUser && currentSocketUser.id !== currentUser.id) {
        console.log("🔄 Socket ID değişmiş, currentUser güncelleniyor:", {
          eski: currentUser.id,
          yeni: currentSocketUser.id,
        });

        // Güncel socket ID ile sender'ı güncelle
        sender.id = String(currentSocketUser.id);

        // currentUser'ı da güncelle (component state'i için)
        setCurrentUser((prev) => ({
          ...prev,
          id: String(currentSocketUser.id),
        }));
      }

      console.log("📤 Mesaj gönderiliyor:", {
        sender,
        message: trimmed,
        "sender.id": sender.id,
        currentUser: currentUser,
        "currentUser.id": currentUser?.id,
        "socketUser.id": currentSocketUser?.id,
      });
      await sendVenueChatMessage(venueUUID, sender, trimmed);

      // Mesaj gönderildikten sonra ek güvenlik scroll'u
      setTimeout(() => {
        scrollToBottom(false);
      }, 100);
    } catch (error) {
      console.error("Mesaj gönderme hatası:", error);
      // Hata durumunda input'u geri yükle
      setInputValue(trimmed);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();

      // Trim kontrolü burada da yap
      if (!inputValue.trim()) {
        return;
      }

      handleSendMessage();
    }
  };

  const triggerFullScreenEmojiAnimation = (emoji, senderName) => {
    const newEmojis = Array.from({ length: 5 }).map((_, index) => ({
      id: `fullscreen-emoji-${Date.now()}-${index}`,
      emoji: emoji,
      senderName: senderName,
      isFullScreen: true,
      // Yüzde bazlı koordinatlar - tüm viewport
      startX: Math.random() * 100, // 0-100%
      startY: Math.random() * 100, // 0-100%
      targetX: Math.random() * 100, // 0-100%
      targetY: Math.random() * 100, // 0-100%
      rotation: Math.random() * 720 - 360,
      scale: 0.1 + Math.random() * 2.5, // 1.5-4 arası
    }));

    setAnimatedEmojis((prev) => [...prev, ...newEmojis]);

    setTimeout(() => {
      setAnimatedEmojis((prev) =>
        prev.filter((e) => !newEmojis.find((ne) => ne.id === e.id))
      );
    }, 4000);
  };

  const handleQuickEmojiSelect = async (emoji) => {
    if (isEmojiCooldown()) {
      return;
    }

    setShowEmojiOptions(false);
    lastEmojiTimeRef.current = Date.now();

    try {
      // 1. Direkt tam sayfa animasyonu tetikle
      triggerFullScreenEmojiAnimation(emoji, currentUser?.name);

      // 2. Emoji'yi kalıcı mesaj olarak da gönder
      if (venueUUID && currentUser) {
        const sender = {
          id: currentUser.id,
          name: currentUser.name,
          avatar: currentUser.avatar,
        };

        // DÜZELTME: Güncel socket ID'yi kullan
        const currentSocketUser = chatSocketService.user;
        if (currentSocketUser && currentSocketUser.id !== currentUser.id) {
          console.log("🔄 Socket ID değişmiş, currentUser güncelleniyor:", {
            eski: currentUser.id,
            yeni: currentSocketUser.id,
          });

          // Güncel socket ID ile sender'ı güncelle
          sender.id = String(currentSocketUser.id);

          // currentUser'ı da güncelle (component state'i için)
          setCurrentUser((prev) => ({
            ...prev,
            id: String(currentSocketUser.id),
          }));
        }

        await sendVenueChatMessage(venueUUID, sender, emoji, "emoji");
      }

      // 3. Diğer kullanıcılara reaction gönder (animasyon için)
      await sendReaction(emoji);
    } catch (error) {
      console.error("Emoji gönderme hatası:", error);
      // Hata durumunda cooldown'ı reset et
      lastEmojiTimeRef.current = 0;
    }
  };

  const handleVibrationClick = async () => {
    if (isVibrationCooldown()) {
      return;
    }

    lastVibrationTimeRef.current = Date.now();

    try {
      // 1. Kendi ekranında titreşim efekti
      triggerVibrationEffect(currentUser?.name);

      // 2. Titreşim mesajını chat'e gönder
      if (venueUUID && currentUser) {
        const sender = {
          id: currentUser.id,
          name: currentUser.name,
          avatar: currentUser.avatar,
        };

        // DÜZELTME: Güncel socket ID'yi kullan
        const currentSocketUser = chatSocketService.user;
        if (currentSocketUser && currentSocketUser.id !== currentUser.id) {
          console.log("🔄 Socket ID değişmiş, currentUser güncelleniyor:", {
            eski: currentUser.id,
            yeni: currentSocketUser.id,
          });

          // Güncel socket ID ile sender'ı güncelle
          sender.id = String(currentSocketUser.id);

          // currentUser'ı da güncelle (component state'i için)
          setCurrentUser((prev) => ({
            ...prev,
            id: String(currentSocketUser.id),
          }));
        }

        const vibrationMessage = `${currentUser.name} tüm odayı titretti! 📳⚡`;
        await sendVenueChatMessage(
          venueUUID,
          sender,
          vibrationMessage,
          "vibration"
        );
      }

      // 3. Diğer kullanıcılara titreşim reaction gönder
      await sendVibration();
    } catch (error) {
      console.error("Vibration gönderme hatası:", error);
      // Hata durumunda cooldown'ı reset et
      lastVibrationTimeRef.current = 0;
    }
  };

  // Scroll handler for infinite scroll - throttled
  const lastScrollTimeRef = useRef(0);
  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container || !hasCheckedIn) return;

    // Throttling - 500ms'de bir kontrol et
    const now = Date.now();
    if (now - lastScrollTimeRef.current < 500) return;
    lastScrollTimeRef.current = now;

    // Yukarı scroll yapıldığında ve container'ın en üstüne yaklaşıldığında
    if (container.scrollTop <= 150 && hasMoreMessages && !isLoadingMore) {
      console.log("Infinite scroll tetiklendi, eski mesajlar yükleniyor...");
      loadMoreMessages();
    }
  };

  const renderTypingIndicator = () => {
    const typingUserDetails = Object.values(typingUsers);
    if (typingUserDetails.length === 0) return null;

    return (
      <div className="absolute bottom-16 left-4 mb-1 flex items-center space-x-2 p-2 bg-white bg-opacity-90 rounded-lg shadow-md max-w-xs animate-fade-in">
        {typingUserDetails.slice(0, 3).map((user, index) => (
          <img
            key={index}
            src={user.avatar || "/assets/qriendManAvatar.png"}
            alt={user.name}
            className="w-5 h-5 rounded-full border border-gray-300 animate-pulse"
            title={user.name}
          />
        ))}
        <div className="flex items-center space-x-1">
          <span className="text-xs text-gray-700 font-medium">
            {typingUserDetails.length === 1
              ? `${typingUserDetails[0].name}`
              : typingUserDetails.length === 2
              ? `${typingUserDetails[0].name} ve ${typingUserDetails[1].name}`
              : `${typingUserDetails.length} kişi`}
          </span>
          <span className="text-xs text-gray-500">yazıyor</span>
          <div className="flex space-x-1">
            <div className="w-1 h-1 bg-gray-500 rounded-full animate-bounce"></div>
            <div
              className="w-1 h-1 bg-gray-500 rounded-full animate-bounce"
              style={{ animationDelay: "0.1s" }}
            ></div>
            <div
              className="w-1 h-1 bg-gray-500 rounded-full animate-bounce"
              style={{ animationDelay: "0.2s" }}
            ></div>
          </div>
        </div>
      </div>
    );
  };

  // Loading States
  if (venueError && venueError.includes("Mekan bulunamadı")) {
    return <ErrorScreen error={venueError} />;
  }

  if (isCheckingIn) {
    return (
      <LoadingScreen
        title="Mekana Giriş Yapılıyor"
        subtitle="Lütfen bekleyiniz..."
      />
    );
  }

  if (checkInError) {
    return <ErrorScreen error={checkInError} />;
  }

  if (isLoggedIn && !hasCheckedIn) {
    return (
      <LoadingScreen
        title="Bağlantı Kuruluyor"
        subtitle="Mekan doğrulaması yapılıyor..."
      />
    );
  }

  // Main Render
  return (
    <div className="mobile-chat-container w-full flex items-center justify-center bg-[#e8e9ed] overflow-hidden">
      <div
        className={`mobile-chat-inner relative flex flex-col items-center w-full max-w-full bg-white shadow-xl overflow-hidden border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl ${
          !isLoggedIn && showModal ? "backdrop-blur-2xl" : ""
        } ${isVibrating ? "animate-shake" : ""}`}
      >
        <ChatScreenHeader />

        <div
          ref={messagesContainerRef}
          className="flex-1 w-full overflow-y-auto p-4 bg-white relative pt-16"
          onScroll={handleScroll}
        >
          {/* Full Screen Emoji Container - React Portal ile body'ye render */}
          {createPortal(
            <div
              className="fixed inset-0 pointer-events-none"
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                zIndex: 9999,
                overflow: "hidden",
              }}
            >
              <AnimatePresence>
                {animatedEmojis.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{
                      left: `${item.startX}vw`,
                      top: `${item.startY}vh`,
                      opacity: 0,
                      scale: 0.1,
                      rotate: 0,
                    }}
                    animate={{
                      left: `${item.targetX}vw`,
                      top: `${item.targetY}vh`,
                      opacity: [0, 0.5, 0.5, 0],
                      scale: [0.1, item.scale, item.scale, 0.1],
                      rotate: [0, item.rotation, item.rotation],
                    }}
                    transition={{
                      duration: 4,
                      ease: "easeOut",
                      times: [0, 0.15, 0.85, 1],
                    }}
                    className="absolute text-8xl pointer-events-none"
                    style={{
                      position: "absolute",
                      transform: "translate(-50%, -50%)",
                      zIndex: 9999,
                      pointerEvents: "none", // Kesinlikle tıklanamaz
                    }}
                  >
                    <div className="relative flex items-center justify-center pointer-events-none">
                      <span
                        style={{
                          fontSize: "4rem",
                          lineHeight: "1",
                          pointerEvents: "none",
                        }}
                      >
                        {item.emoji}
                      </span>
                      {item.senderName && (
                        <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-xs font-medium text-gray-600 whitespace-nowrap bg-white bg-opacity-80 px-2 py-1 rounded-full opacity-60 pointer-events-none">
                          {item.senderName}
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>,
            document.body
          )}

          {isLoading ? (
            <div className="flex justify-center items-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E94057]"></div>
            </div>
          ) : error ? (
            <div className="flex justify-center items-center h-full text-red-500">
              {error}
            </div>
          ) : (
            <div className="space-y-4 relative z-10">
              {/* Infinite Scroll Loading Indicator */}
              {isLoadingMore && (
                <div className="flex justify-center items-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E94057]"></div>
                  <span className="ml-2 text-sm text-gray-500">
                    Eski mesajlar yükleniyor...
                  </span>
                </div>
              )}

              {/* No More Messages Indicator */}
              {!hasMoreMessages && messages.length > 10 && (
                <div className="flex justify-center items-center py-2">
                  <span className="text-xs text-gray-400">
                    Tüm mesajlar yüklendi
                  </span>
                </div>
              )}

              {/* Demo Messages for Non-logged Users */}
              {showModal &&
                !isLoggedIn &&
                animatedMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className="flex flex-col items-start mt-2 animate-fade-in"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <img
                        src={msg.avatar}
                        alt={msg.name}
                        className="w-6 h-6 rounded-full border border-gray-300"
                      />
                      <span className="text-xs font-semibold text-black font-poppins">
                        {msg.name}
                      </span>
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="bg-gray-200 py-2 px-3 rounded-xl max-w-[40ch] text-sm text-black font-poppins break-words">
                        {msg.text}
                      </div>
                      <span className="text-xs text-gray-500 opacity-80 font-poppins">
                        {msg.time}
                      </span>
                    </div>
                  </div>
                ))}

              {/* Real Messages - Using ChatMessage Component */}
              {messages.map((message, index) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  index={index}
                  messages={messages}
                  currentUser={currentUser}
                  animatedVibrationMessages={animatedVibrationMessages}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Typing Indicator */}
        {renderTypingIndicator()}

        {/* Input Area */}
        <div className="w-full p-4 bg-white border-t">
          <div className="flex items-center gap-3 max-w-full overflow-hidden">
            <div className="relative">
              <button
                className="focus:outline-none bg-[#FFEBF0] hover:bg-[#ffdbdb] rounded-full p-2 transition-colors duration-200"
                type="button"
                onClick={() => {
                  setShowEmojiOptions(!showEmojiOptions);
                }}
              >
                <PiHandsClappingLight className="w-5 h-5 text-[#E94057]" />
              </button>
            </div>

            {/* Vibration Button */}
            <div className="relative">
              <button
                className={`focus:outline-none bg-[#FFF3E0] hover:bg-[#FFE0B2] rounded-full p-2 transition-colors duration-200 ${
                  isVibrationCooldown() ? "opacity-50 cursor-not-allowed" : ""
                } ${isVibrating ? "animate-pulse" : ""}`}
                type="button"
                onClick={handleVibrationClick}
                disabled={isVibrationCooldown()}
                title="Titreşim Gönder"
              >
                <HiDevicePhoneMobile className="w-5 h-5 text-[#FF9800]" />
              </button>
            </div>

            {/* Alternative quick emoji display - temporary for testing */}
            {showEmojiOptions && (
              <div className="fixed bottom-20 left-4 bg-white rounded-full shadow-lg border border-gray-200 p-3 flex gap-3 items-center z-[100]">
                {quickEmojiOptions.map((emoji) => (
                  <button
                    key={`fixed-${emoji}`}
                    className={`hover:scale-125 transition-transform duration-200 text-2xl px-2 py-1 rounded-full hover:bg-gray-100 ${
                      isEmojiCooldown() ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    disabled={isEmojiCooldown()}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const selectedEmoji = emoji; // Capture emoji value
                      if (!isEmojiCooldown()) {
                        handleQuickEmojiSelect(selectedEmoji);
                      }
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}

            <input
              type="text"
              placeholder="Lütfen bir mesaj yazın"
              className="flex-1 bg-white rounded-full px-4 py-2 text-gray-500 font-poppins text-base shadow-sm outline-none border-none min-w-0"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
            />

            <button
              className="p-1 text-gray-400 ml-2 focus:outline-none flex-shrink-0 w-8 h-8 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSendMessage}
              disabled={inputValue.trim() === ""}
            >
              <HiPaperAirplane className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Login/Register Modal */}
        {showModal && !isLoggedIn && (
          <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur">
            <div className="relative bg-white rounded-3xl shadow-xl px-6 pt-8 pb-6 w-[90%] max-w-xs flex flex-col items-center animate-fade-in z-10">
              <div className="absolute -top-7 left-1/2 -translate-x-1/2 flex flex-col items-center">
                <div className="rounded-2xl px-6 py-2 flex flex-col items-center justify-center">
                  <img
                    src={venueDetails?.logo || "/assets/qriendPhoneTanitim.png"}
                    alt={venueDetails?.name || "Mekan"}
                    className="w-16 h-16 mb-1"
                  />
                </div>
              </div>
              <div className="mt-6 text-center">
                <h2 className="text-xl font-semibold font-poppins text-black mb-2">
                  {venueDetails?.name || "Mekan Bulunamadı"}
                </h2>
                {venueError ? (
                  <p className="text-red-600 font-poppins text-base mb-4">
                    {venueError}
                  </p>
                ) : null}
                <p className="text-black font-poppins text-base mb-6">
                  İşletmenin özel grup sohbetine katılmak için giriş yapınız
                  veya kayıt olunuz.
                </p>
                <Link
                  to="/login"
                  state={{
                    from: `/v/${venueUUID}`,
                    venueUUID: venueUUID,
                  }}
                >
                  <button className="w-full bg-[#E94057] text-white text-[16px] font-normal rounded-full py-2 mb-2 transition hover:bg-[#E94057] font-poppins">
                    Giriş Yap
                  </button>
                </Link>
                <Link
                  to="/register"
                  state={{
                    from: `/v/${venueUUID}`,
                    venueUUID: venueUUID,
                  }}
                >
                  <button className="w-full text-[16px] border border-[#E94057] text-[#E94057] font-normal rounded-full py-2 bg-white transition hover:bg-pink-50 font-poppins">
                    Kayıt Ol
                  </button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatScreen;
